<template>
  <div class="filtro-coluna">
    <div class="filtro-ordenacao">
      <div class="ordenacao-label">
        <v-icon x-small color="#64748b" class="mr-1">mdi-sort</v-icon>
        <span>Ordenar</span>
      </div>
      <div class="ordenacao-botoes">
        <v-btn
            small
            depressed
            :color="ordenacaoSelecionada === 'asc' ? '#023449' : ''"
            :outlined="ordenacaoSelecionada !== 'asc'"
            :dark="ordenacaoSelecionada === 'asc'"
            :class="{ 'btn-inativo': ordenacaoSelecionada !== 'asc' }"
            class="ordenacao-btn"
            @click="aplicarOrdenacaoImediata('asc')"
        >
          <v-icon small class="mr-1">{{ iconOrdenacaoAsc }}</v-icon>
          {{ labelOrdenacaoAsc }}
        </v-btn>
        <v-btn
            small
            depressed
            :color="ordenacaoSelecionada === 'desc' ? '#023449' : ''"
            :outlined="ordenacaoSelecionada !== 'desc'"
            :dark="ordenacaoSelecionada === 'desc'"
            :class="{ 'btn-inativo': ordenacaoSelecionada !== 'desc' }"
            class="ordenacao-btn"
            @click="aplicarOrdenacaoImediata('desc')"
        >
          <v-icon small class="mr-1">{{ iconOrdenacaoDesc }}</v-icon>
          {{ labelOrdenacaoDesc }}
        </v-btn>
      </div>
    </div>

    <v-divider/>

    <div v-if="tipo === 'texto'" class="filtro-body">
      <v-text-field
          v-model="buscaTexto"
          dense outlined hide-details
          placeholder="Digite para buscar..."
          prepend-inner-icon="mdi-magnify"
          clearable
          class="mb-3 search-field"
      />

      <div v-if="selecionados.length > 0" class="filtro-info-selecionados">
        <div class="info-text">
          <v-icon x-small color="#5b21b6" class="mr-1">mdi-check-circle</v-icon>
          {{ selecionados.length }} selecionado{{ selecionados.length > 1 ? 's' : '' }}
        </div>
        <v-btn x-small text color="#dc2626" @click="limpar" class="clear-selection-btn">
          <v-icon x-small class="mr-1">mdi-close</v-icon>
          Limpar
        </v-btn>
      </div>

      <div v-if="buscaTexto && valoresFiltrados.length > 0 && valoresFiltrados.length < valores.length" class="filtro-info">
        <v-icon x-small color="#64748b" class="mr-1">mdi-information-outline</v-icon>
        {{ valoresFiltrados.length }} resultado{{ valoresFiltrados.length > 1 ? 's' : '' }} da busca
      </div>

      <div v-if="buscaTexto && valoresFiltrados.length === 0" class="filtro-info-empty">
        <v-icon small color="#94a3b8">mdi-magnify-close</v-icon>
        <span>Nenhum resultado encontrado</span>
      </div>

      <div class="valores-list">
        <v-checkbox
            v-model="todosSelecionados"
            label="Selecionar todos visíveis"
            hide-details dense
            color="#023449"
            class="ma-0 checkbox-todos"
            @change="toggleTodos"
        />
        <div class="valores-scroll" ref="scrollContainer" @scroll="handleScroll">
          <v-checkbox
              v-for="valor in valoresVisiveis"
              :key="valor"
              :value="valor"
              v-model="selecionados"
              :label="String(valor)"
              hide-details dense
              color="#023449"
              class="ma-0 checkbox-item"
          />

          <div v-if="carregandoMais" class="loading-more">
            <v-progress-circular indeterminate size="20" width="2" color="#023449"/>
            <span>Carregando mais...</span>
          </div>

          <div v-if="!carregandoMais && valoresVisiveis.length >= valores.length && valores.length > limitePorPagina" class="end-indicator">
            <v-icon x-small color="#94a3b8">mdi-check</v-icon>
            <span>Todos os itens carregados</span>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="tipo === 'data' || tipo === 'selecao'" class="filtro-body">
      <div v-if="selecionados.length > 0" class="filtro-info-selecionados">
        <div class="info-text">
          <v-icon x-small color="#5b21b6" class="mr-1">mdi-check-circle</v-icon>
          {{ selecionados.length }} selecionado{{ selecionados.length > 1 ? 's' : '' }}
        </div>
        <v-btn x-small text color="#dc2626" @click="limpar" class="clear-selection-btn">
          <v-icon x-small class="mr-1">mdi-close</v-icon>
          Limpar
        </v-btn>
      </div>

      <div v-if="valores.length > limitePorPagina" class="filtro-info">
        <v-icon x-small color="#64748b" class="mr-1">mdi-information-outline</v-icon>
        Mostrando {{ valoresVisiveis.length }} de {{ valores.length }} itens
      </div>

      <div class="valores-list">
        <v-checkbox
            v-model="todosSelecionados"
            label="Selecionar todos visíveis"
            hide-details dense
            color="#023449"
            class="ma-0 checkbox-todos"
            @change="toggleTodos"
        />
        <div class="valores-scroll" ref="scrollContainer" @scroll="handleScroll">
          <v-checkbox
              v-for="valor in valoresVisiveis"
              :key="valor"
              :value="valor"
              v-model="selecionados"
              :label="formatarLabel(valor)"
              hide-details dense
              color="#023449"
              class="ma-0 checkbox-item"
          />

          <div v-if="carregandoMais" class="loading-more">
            <v-progress-circular indeterminate size="20" width="2" color="#023449"/>
            <span>Carregando mais...</span>
          </div>

          <div v-if="!carregandoMais && valoresVisiveis.length >= valores.length && valores.length > limitePorPagina" class="end-indicator">
            <v-icon x-small color="#94a3b8">mdi-check</v-icon>
            <span>Todos os itens carregados</span>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="tipo === 'numero' || tipo === 'moeda' || tipo === 'percentual'" class="filtro-body">
      <div class="range-inputs">
        <v-text-field
            v-model.number="rangeMin"
            type="number"
            dense outlined hide-details
            :label="tipo === 'percentual' ? 'Mínimo (%)' : 'Mínimo'"
            :prefix="tipo === 'moeda' ? 'R$' : ''"
            :suffix="tipo === 'percentual' ? '%' : ''"
            class="mb-2 range-field"
        />
        <v-text-field
            v-model.number="rangeMax"
            type="number"
            dense outlined hide-details
            :label="tipo === 'percentual' ? 'Máximo (%)' : 'Máximo'"
            :prefix="tipo === 'moeda' ? 'R$' : ''"
            :suffix="tipo === 'percentual' ? '%' : ''"
            class="range-field"
        />
      </div>
      <div class="range-info">
        <v-icon x-small color="#64748b">mdi-information-outline</v-icon>
        <span>Deixe em branco para não limitar</span>
      </div>

      <div v-if="rangeMin !== null || rangeMax !== null" class="range-preview">
        <v-icon x-small color="#5b21b6" class="mr-1">mdi-filter-check</v-icon>
        <span v-if="rangeMin !== null && rangeMax !== null">
          {{ formatarValor(rangeMin) }} até {{ formatarValor(rangeMax) }}
        </span>
        <span v-else-if="rangeMin !== null">
          Maior ou igual a {{ formatarValor(rangeMin) }}
        </span>
        <span v-else>
          Menor ou igual a {{ formatarValor(rangeMax) }}
        </span>
      </div>
    </div>

    <v-divider/>
    <div class="filtro-footer">
      <v-btn
          x-small text
          color="#64748b"
          @click="limpar"
          :disabled="!temFiltroAtivo"
          class="footer-clear-btn"
      >
        <v-icon x-small class="mr-1">mdi-filter-remove</v-icon>
        Limpar
      </v-btn>
      <v-spacer/>
      <v-btn
          x-small depressed
          @click="aplicar"
          :disabled="!podeFiltrar"
          class="footer-apply-btn primary elevation-0"
      >
        <v-icon x-small class="mr-1">mdi-check</v-icon>
        Aplicar
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FiltroColuna',

  props: {
    campo: {
      type: String,
      required: true,
    },
    tipo: {
      type: String,
      required: true,
      validator: v => ['texto', 'numero', 'moeda', 'percentual', 'data', 'selecao'].includes(v),
    },
    valores: {
      type: Array,
      default: () => [],
    },
    filtroAtivo: {
      type: [Array, Object],
      default: () => [],
    },
    ordenacaoAtiva: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      selecionados: [],
      todosSelecionados: false,
      buscaTexto: '',
      rangeMin: null,
      rangeMax: null,
      limitePorPagina: 50,
      paginaAtual: 1,
      carregandoMais: false,
      ordenacaoSelecionada: null,
    };
  },

  computed: {
    valoresFiltrados() {
      if (this.tipo === 'texto' && this.buscaTexto) {
        const termo = this.buscaTexto.toLowerCase();
        return this.valores.filter(v =>
            String(v).toLowerCase().includes(termo)
        );
      }
      return [...this.valores];
    },

    valoresVisiveis() {
      const lista = this.valoresFiltrados;
      const limite = this.paginaAtual * this.limitePorPagina;
      return lista.slice(0, limite);
    },

    temMaisItens() {
      return this.valoresVisiveis.length < this.valores.length;
    },

    temFiltroAtivo() {
      if (Array.isArray(this.filtroAtivo)) {
        return this.filtroAtivo.length > 0;
      }
      if (typeof this.filtroAtivo === 'object') {
        return this.filtroAtivo.min !== null || this.filtroAtivo.max !== null;
      }
      return false;
    },

    podeFiltrar() {
      if (this.tipo === 'texto' || this.tipo === 'data' || this.tipo === 'selecao') {
        return this.selecionados.length > 0;
      }
      return this.rangeMin !== null || this.rangeMax !== null;
    },

    labelOrdenacaoAsc() {
      if (this.tipo === 'numero' || this.tipo === 'moeda' || this.tipo === 'percentual') {
        return 'Menor';
      }
      if (this.tipo === 'data') {
        return 'Antiga';
      }
      return 'A-Z';
    },

    labelOrdenacaoDesc() {
      if (this.tipo === 'numero' || this.tipo === 'moeda' || this.tipo === 'percentual') {
        return 'Maior';
      }
      if (this.tipo === 'data') {
        return 'Recente';
      }
      return 'Z-A';
    },

    iconOrdenacaoAsc() {
      if (this.tipo === 'numero' || this.tipo === 'moeda' || this.tipo === 'percentual') {
        return 'mdi-sort-numeric-ascending';
      }
      if (this.tipo === 'data') {
        return 'mdi-sort-calendar-ascending';
      }
      return 'mdi-sort-alphabetical-ascending';
    },

    iconOrdenacaoDesc() {
      if (this.tipo === 'numero' || this.tipo === 'moeda' || this.tipo === 'percentual') {
        return 'mdi-sort-numeric-descending';
      }
      if (this.tipo === 'data') {
        return 'mdi-sort-calendar-descending';
      }
      return 'mdi-sort-alphabetical-descending';
    },
  },

  watch: {
    filtroAtivo: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val)) {
          this.selecionados = [...val];
          this.atualizarTodosSelecionados();
        } else if (typeof val === 'object') {
          this.rangeMin = val.min;
          this.rangeMax = val.max;
        }
      },
    },

    ordenacaoAtiva: {
      immediate: true,
      handler(val) {
        this.ordenacaoSelecionada = val ?? null;
      },
    },

    selecionados() {
      this.atualizarTodosSelecionados();
    },

    valores: {
      immediate: true,
      handler() {
        this.resetarPaginacao();
      },
    },

    buscaTexto() {
      this.resetarPaginacao();
    }
  },

  methods: {
    handleScroll(event) {
      if (this.carregandoMais || !this.temMaisItens) return;

      const container = event.target;
      const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

      if (scrollBottom < 100) {
        this.carregarMais();
      }
    },

    carregarMais() {
      if (this.carregandoMais || !this.temMaisItens) return;

      this.carregandoMais = true;

      setTimeout(() => {
        this.paginaAtual++;
        this.carregandoMais = false;
      }, 200);
    },

    resetarPaginacao() {
      this.paginaAtual = 1;
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollTop = 0;
      }
    },

    aplicarOrdenacaoImediata(direcao) {
      this.ordenacaoSelecionada = direcao;
      this.$emit('ordenar', direcao);
    },

    aplicar() {
      if (this.tipo === 'texto' || this.tipo === 'data' || this.tipo === 'selecao') {
        this.$emit('aplicar', [...this.selecionados]);
      } else {
        this.$emit('aplicar', {
          min: this.rangeMin !== '' && this.rangeMin !== null ? Number(this.rangeMin) : null,
          max: this.rangeMax !== '' && this.rangeMax !== null ? Number(this.rangeMax) : null,
        });
      }
    },

    limpar() {
      if (this.tipo === 'texto' || this.tipo === 'data' || this.tipo === 'selecao') {
        this.selecionados = [];
        this.buscaTexto = '';
        this.todosSelecionados = false;
        this.resetarPaginacao();
      } else {
        this.rangeMin = null;
        this.rangeMax = null;
      }
      this.$emit('limpar');
    },

    limparSelecionados() {
      this.selecionados = [];
      this.todosSelecionados = false;
      this.buscaTexto = '';
      this.resetarPaginacao();
    },

    toggleTodos() {
      if (this.todosSelecionados) {
        const novosItens = this.valoresVisiveis.filter(v => !this.selecionados.includes(v));
        this.selecionados = [...this.selecionados, ...novosItens];
      } else {
        this.selecionados = this.selecionados.filter(v => !this.valoresVisiveis.includes(v));
      }
    },

    atualizarTodosSelecionados() {
      if (this.valoresVisiveis.length === 0) {
        this.todosSelecionados = false;
        return;
      }

      this.todosSelecionados = this.valoresVisiveis.every(v =>
          this.selecionados.includes(v)
      );
    },

    formatarLabel(valor) {
      if (this.tipo === 'selecao' && this.campo === 'status_principal') {
        if (!valor) return '?';

        if (valor === 'FORA DE LINHA') return '⚫ Fora de Linha';
        if (valor === 'CORTADO - Sem Estoque CD') return '🟠 Cortado';
        if (valor === 'RUPTURA TOTAL') return '🔴 Rupt. Total';
        if (valor === 'RUPTURA PARCIAL') return '🟠 Rupt. Parcial';
        if (valor === 'ATENDIDO OK') return '🟢 Atendido OK';
        if (valor === 'Sugestão Ignorada') return '🔵 Sugestão Ignorada';

        return valor;
      }

      return String(valor);
    },

    formatarValor(valor) {
      if (this.tipo === 'moeda') {
        return `R$ ${Number(valor).toFixed(2)}`;
      }
      if (this.tipo === 'percentual') {
        return `${Number(valor).toFixed(1)}%`;
      }
      return String(valor);
    },
  },
};
</script>

<style scoped>
.filtro-coluna {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(2, 52, 73, 0.15);
  min-width: 320px;
  max-width: 380px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.filtro-ordenacao {
  padding: 12px 16px;
  background: #fafbfc;
}

.ordenacao-label {
  display: flex;
  align-items: center;
  font-size: 0.688rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
}

.ordenacao-botoes {
  display: flex;
  gap: 8px;
}

.ordenacao-btn {
  flex: 1;
  text-transform: none !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  height: 32px !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.ordenacao-btn:not(.btn-inativo) {
  background-color: #023449 !important;
  color: #ffffff !important;
}

.ordenacao-btn.btn-inativo {
  color: #94a3b8 !important;
  border: 1.5px solid #e2e8f0 !important;
  background: #ffffff !important;
}

.ordenacao-btn:hover:not(.btn-inativo) {
  background-color: #035a7a !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(2, 52, 73, 0.2);
}

.ordenacao-btn:hover.btn-inativo {
  background-color: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.ordenacao-btn .v-icon {
  font-size: 16px !important;
}

.filtro-body {
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.filtro-body::-webkit-scrollbar {
  width: 6px;
}

.filtro-body::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.filtro-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.filtro-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.search-field >>> fieldset {
  border-color: #cbd5e1;
  border-width: 2px;
  transition: all 0.2s ease;
}

.search-field >>> .v-input__slot {
  background: #f8fafc;
}

.search-field:hover >>> fieldset {
  border-color: #023449;
}

.search-field >>> .v-input--is-focused fieldset {
  border-color: #023449 !important;
}

.filtro-info {
  display: flex;
  align-items: center;
  font-size: 0.688rem;
  color: #64748b;
  padding: 8px 10px;
  background: #f1f5f9;
  border-radius: 6px;
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
}

.filtro-info-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  background: #f8fafc;
  border-radius: 8px;
  color: #94a3b8;
  font-size: 0.75rem;
}

.filtro-info-selecionados {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%);
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1.5px solid #e9d5ff;
}

.info-text {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #5b21b6;
}

.clear-selection-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.688rem !important;
}

.valores-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.checkbox-todos {
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}

.checkbox-todos >>> .v-label {
  font-weight: 700;
  color: #023449;
  font-size: 0.813rem;
}

.valores-scroll {
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 4px;
}

.valores-scroll::-webkit-scrollbar {
  width: 5px;
}

.valores-scroll::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.valores-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.valores-scroll::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.checkbox-item {
  padding: 6px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.checkbox-item:hover {
  background-color: rgba(2, 52, 73, 0.04);
}

.checkbox-item >>> .v-label {
  font-size: 0.75rem;
  color: #334155;
  white-space: normal !important;
  word-break: break-word;
  line-height: 1.3;
}

.checkbox-item >>> .v-input--selection-controls__input {
  align-self: flex-start;
  margin-top: 2px;
}

.loading-more,
.end-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  font-size: 0.75rem;
  color: #64748b;
  animation: fadeIn 0.3s ease-in;
}

.end-indicator {
  color: #94a3b8;
  font-size: 0.688rem;
}

.range-inputs {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.range-field >>> fieldset {
  border-color: #cbd5e1;
  border-width: 2px;
}

.range-field >>> .v-input__slot {
  background: #f8fafc;
}

.range-field:hover >>> fieldset {
  border-color: #023449;
}

.range-field >>> .v-input--is-focused fieldset {
  border-color: #023449 !important;
}

.range-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 10px;
  background: #f1f5f9;
  border-radius: 8px;
  font-size: 0.688rem;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.range-preview {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%);
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #5b21b6;
  border: 1.5px solid #e9d5ff;
}

.filtro-footer {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.footer-clear-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.688rem !important;
  letter-spacing: 0;
}

.footer-apply-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 0.688rem !important;
  letter-spacing: 0;
  background-color: #023449 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.2);
  transition: all 0.2s ease;
}

.footer-apply-btn:hover:not(.v-btn--disabled) {
  background-color: #035a7a !important;
  box-shadow: 0 4px 12px rgba(2, 52, 73, 0.3);
  transform: translateY(-1px);
}

.footer-apply-btn >>> .v-btn__content {
  color: #ffffff !important;
}

.footer-apply-btn >>> .v-icon {
  color: #ffffff !important;
}

.filtro-coluna >>> .v-input--checkbox .v-input--selection-controls__input {
  margin-right: 8px;
}

.filtro-coluna >>> .v-input--checkbox {
  margin-top: 0;
  padding-top: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>